import { useQuery } from "@tanstack/react-query";
import {
  addDays,
  addMonths,
  differenceInDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getISOWeek,
  getISOWeekYear,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
} from "date-fns";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View, useWindowDimensions } from "react-native";

import {
  type DayInvalidReasons,
  MealplanHeader,
  MonthlyDataState,
  PreselectionContainer,
  WeeklyGrid,
  WeeklyGridHeader,
  type WeeklyMenuData,
  validateMealPlan,
} from "@luna-restaurant/feature-luna-school-menu-plan";
import { MenuItem as MenuItemType } from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/types";
import { useTranslation } from "@luna-restaurant/i18n";
import {
  type ClosingTime,
  type DishPlan,
  type DishPlanDocument,
  type DishPlanItem,
  type JsonLdCollection,
  type SchoolDishPlan,
  type SchoolDishPlanInput,
} from "@luna-restaurant/school-api";
import {
  Button,
  Icon,
  NoticeBox,
  PageContainer,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { ContactBanner } from "@/components/ContactBanner";
import { SecondaryButton } from "@/components/SecondaryButton";
import { WeeklyGridLoadingSkeleton } from "@/components/mealPlan/WeeklyGridLoadingSkeleton";
import { ChefHatModal } from "@/components/modals/ChefHatModal";
import { useCollection } from "@/hooks/useCollection";
import { useCurrentSchool } from "@/hooks/useCurrentSchool";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLayoutScroll } from "@/hooks/useLayoutScroll";
import { useRequest } from "@/hooks/useRequest";
import {
  DISH_PLAN_STALE_TIME_MS,
  type NormalizedPlanItem,
  formatDateKey,
  getPlanItems,
  normalizePlanItem,
} from "@/utils/dishPlanHelpers";

type YearMonthQueryParamKey = "year-month" | "jahr-monat";

const NO_STORE_COLLECTION_REQUEST_OPTIONS = {
  cache: "no-store" as RequestCache,
};

const weekdayKeyByIndex: Record<number, keyof WeeklyMenuData["days"]> = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
};

const createEmptyMenuData = (
  dayNumber: number,
  month: number,
  isSchoolHoliday?: boolean,
) => ({
  dayNumber,
  month,
  menus: [],
  desserts: [],
  isSchoolHoliday,
});

type ClosingStatus = "publicHoliday" | "schoolClosed" | "schoolHoliday";

const closingTypePriority: Record<ClosingStatus, number> = {
  publicHoliday: 3,
  schoolClosed: 2,
  schoolHoliday: 1,
};

const mapClosingType = (type: ClosingTime["type"]): ClosingStatus => {
  switch (type) {
    case "PUBLIC_HOLIDAY":
      return "publicHoliday";
    case "HOLIDAY":
      return "schoolHoliday";
    default:
      return "schoolClosed";
  }
};

type ClosingTimeInfo = {
  status: ClosingStatus;
  name?: string;
};

export const buildClosingTimeMap = (
  closingTimes: ClosingTime[],
  start: Date,
  end: Date,
): Map<string, ClosingTimeInfo> => {
  const map = new Map<string, ClosingTimeInfo>();

  for (const closingTime of closingTimes) {
    const status = mapClosingType(closingTime.type);

    const startDate = parseISO(closingTime.startDate);
    const endDate = parseISO(closingTime.endDate);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      continue;
    }

    const intervalStart = startDate > start ? startDate : start;
    const intervalEnd = endDate < end ? endDate : end;
    if (intervalStart > intervalEnd) {
      continue;
    }

    for (const date of eachDayOfInterval({
      start: intervalStart,
      end: intervalEnd,
    })) {
      const key = formatDateKey(date);
      const existing = map.get(key);
      if (
        !existing ||
        closingTypePriority[status] > closingTypePriority[existing.status]
      ) {
        map.set(key, { status, name: closingTime.name ?? undefined });
      }
    }
  }

  return map;
};

export const buildWeeklyMenuData = (
  plansOrItems: (DishPlan | DishPlanItem[] | null | undefined)[],
  targetMonth: Date,
  closingTimes?: ClosingTime[],
  planningMonthKeys: Set<string> = new Set(),
): WeeklyMenuData[] => {
  const start = startOfWeek(startOfMonth(targetMonth), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(targetMonth), { weekStartsOn: 1 });
  const closingTimesByDate = buildClosingTimeMap(
    closingTimes ?? [],
    start,
    end,
  );

  const normalizedItems = plansOrItems
    .flatMap((entry) => (Array.isArray(entry) ? entry : getPlanItems(entry)))
    .map((item) => normalizePlanItem(item, start, end))
    .filter((item): item is NormalizedPlanItem => item !== null);

  const itemsByDate = new Map<string, NormalizedPlanItem[]>();

  for (const item of normalizedItems) {
    const key = formatDateKey(item.date);
    const collection = itemsByDate.get(key);

    if (collection) {
      collection.push(item);
    } else {
      itemsByDate.set(key, [item]);
    }
  }

  const weeksMap = new Map<string, WeeklyMenuData>();
  const metaMap = new Map<string, { hasTargetDay: boolean }>();

  for (const date of eachDayOfInterval({ start, end })) {
    const weekdayIndex = date.getDay();
    const dayKey = weekdayKeyByIndex[weekdayIndex];

    if (!dayKey) {
      continue;
    }

    const isoWeek = getISOWeek(date);
    const isoWeekYear = getISOWeekYear(date);
    const month = date.getMonth();
    const weekKey = `${isoWeekYear}-${isoWeek}`;

    if (!weeksMap.has(weekKey)) {
      weeksMap.set(weekKey, {
        weekNumber: isoWeek,
        year: isoWeekYear,
        days: {
          monday: createEmptyMenuData(0, month),
          tuesday: createEmptyMenuData(0, month),
          wednesday: createEmptyMenuData(0, month),
          thursday: createEmptyMenuData(0, month),
          friday: createEmptyMenuData(0, month),
        },
      });
      metaMap.set(weekKey, { hasTargetDay: false });
    }

    const week = weeksMap.get(weekKey)!;
    const meta = metaMap.get(weekKey)!;
    const dayNumber = date.getDate();
    const dateKey = formatDateKey(date);
    const items = itemsByDate.get(dateKey);
    const isInTargetMonth = isSameMonth(date, targetMonth);

    const closingInfo = closingTimesByDate.get(dateKey);
    const closingStatus = closingInfo?.status;
    const dateMonthKey = format(date, "yyyy-MM");
    const isPlanningMonth = planningMonthKeys.has(dateMonthKey);
    const shouldShowSchoolHolidayIndicator =
      closingStatus === "schoolHoliday" && Boolean(items?.length);

    // Public holidays and explicit NO_FOOD days always render as full no-menu cards.
    if (closingStatus === "publicHoliday" || closingStatus === "schoolClosed") {
      week.days[dayKey] = {
        dayNumber,
        type: closingStatus,
        month,
        ...(closingInfo?.name ? { title: closingInfo.name } : {}),
      };
    } else if (isPlanningMonth && !items?.length) {
      week.days[dayKey] = {
        dayNumber,
        type: "planning",
        month,
        showSchoolHolidayIndicator: closingStatus === "schoolHoliday",
      };
    } else if (items?.length) {
      const sortedItems = [...items].sort((a, b) => a.position - b.position);
      const menus = [] as MenuItemType[];
      const desserts: string[] = [];

      for (const item of sortedItems) {
        if (item.isDessert) {
          desserts.push(item.title);
          continue;
        }

        menus.push({
          "@id": item["@id"],
          id: item.id,
          title: item.title,
          label: item.label,
          isSelected: false,
          position: item.position,
        });
      }

      if (menus.length > 0 || desserts.length > 0) {
        week.days[dayKey] = {
          dayNumber,
          month,
          menus,
          desserts,
          isSchoolHoliday: shouldShowSchoolHolidayIndicator,
        };
      } else {
        week.days[dayKey] = createEmptyMenuData(
          dayNumber,
          month,
          shouldShowSchoolHolidayIndicator,
        );
      }
    } else {
      week.days[dayKey] = createEmptyMenuData(
        dayNumber,
        month,
        shouldShowSchoolHolidayIndicator,
      );
    }

    if (isInTargetMonth) {
      meta.hasTargetDay = true;
    }
  }

  return Array.from(weeksMap.entries())
    .filter(([weekKey]) => Boolean(metaMap.get(weekKey)?.hasTargetDay))
    .map(([, week]) => week)
    .sort((a, b) => {
      if (a.year === b.year) {
        return a.weekNumber - b.weekNumber;
      }

      return a.year - b.year;
    });
};

const buildMenuSelectionMap = (weeks: WeeklyMenuData[]) => {
  const selections = new Map<number, boolean>();

  for (const week of weeks) {
    for (const day of Object.values(week.days)) {
      if (!("menus" in day)) {
        continue;
      }

      for (const menu of day.menus) {
        selections.set(menu.id, menu.isSelected);
      }
    }
  }

  return selections;
};

const applyMenuSelections = (
  weeks: WeeklyMenuData[],
  selections: Map<number, boolean>,
): WeeklyMenuData[] =>
  weeks.map((week) => ({
    ...week,
    days: (Object.keys(week.days) as (keyof WeeklyMenuData["days"])[]).reduce(
      (acc, key) => {
        const day = week.days[key];

        if (!day || !("menus" in day)) {
          acc[key] = day;
          return acc;
        }

        acc[key] = {
          ...day,
          menus: day.menus.map((menu) =>
            selections.has(menu.id)
              ? { ...menu, isSelected: selections.get(menu.id) ?? false }
              : menu,
          ),
        };

        return acc;
      },
      {} as WeeklyMenuData["days"],
    ),
  }));

const buildYearMonthKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

export const buildSchoolDishPlanPayload = (
  weeks: WeeklyMenuData[],
  targetMonth: Date,
): SchoolDishPlanInput => {
  const selectedMenuItemIris = new Set<string>();
  const targetMonthIndex = targetMonth.getMonth();

  for (const week of weeks) {
    for (const day of Object.values(week.days)) {
      if (!("menus" in day) || day.month !== targetMonthIndex) {
        continue;
      }

      for (const menu of day.menus) {
        if (!menu.isSelected) {
          continue;
        }
        selectedMenuItemIris.add(menu["@id"]);
      }
    }
  }

  return {
    items: Array.from(selectedMenuItemIris),
    month: targetMonth.getMonth() + 1,
    year: targetMonth.getFullYear(),
  };
};

export default function MealPlanScreen() {
  const { colors, fonts } = useCustomTheme();
  const { i18n, t } = useTranslation();
  const router = useRouter();
  const request = useRequest();
  const { currentUser } = useCurrentUser();
  const queryParamKey = t(
    "mealPlan.yearMonthQueryParamKey",
  ) as YearMonthQueryParamKey;
  const params =
    useLocalSearchParams<Partial<Record<YearMonthQueryParamKey, string>>>();

  const getInitialDate = () => {
    const now = new Date();
    const yearMonthParam = params?.[queryParamKey]?.split("-");
    const year = yearMonthParam ? Number(yearMonthParam[0]) : now.getFullYear();
    const month = yearMonthParam
      ? Number(yearMonthParam[1]) - 1
      : now.getMonth();

    if (!isNaN(year) && !isNaN(month) && month >= 0 && month < 12) {
      const newDate = new Date(now);
      newDate.setFullYear(year);
      newDate.setMonth(month);
      return newDate;
    }

    return now;
  };

  const [selectedMonth, setSelectedMonth] = useState(getInitialDate);
  const [pendingMonth, setPendingMonth] = useState<Date | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyDataState>({});
  const { width } = useWindowDimensions();
  const maxWidth = 1728;

  const [locallyPublishedMonthKey, setLocallyPublishedMonthKey] = useState<
    string | null
  >(null);
  const [schoolDishPlanItemIdsByMonth, setSchoolDishPlanItemIdsByMonth] =
    useState<Record<string, number[]>>({});
  const [hasPublishedFromServerByMonth, setHasPublishedFromServerByMonth] =
    useState<Record<string, boolean>>({});
  const [publishModalState, setPublishModalState] = useState<
    "success" | "validationError" | "requestError" | null
  >(null);
  const [publishErrorMessage, setPublishErrorMessage] = useState<string | null>(
    null,
  );
  const [isPublishing, setIsPublishing] = useState(false);

  const [invalidDates, setInvalidDates] = useState<
    Record<`${number}-${number}`, DayInvalidReasons>
  >({});

  const { scrollToTop } = useLayoutScroll();

  const currentMonthKey = buildYearMonthKey(selectedMonth);
  const queryMonth = pendingMonth ?? selectedMonth;
  const queryMonthKey = buildYearMonthKey(queryMonth);

  const { currentSchool, isLoadingSchool } = useCurrentSchool();
  const isNormalSchool = isLoadingSchool
    ? true
    : (currentSchool?.minimumSelectionPolicy ?? "TWO_REQUIRED") ===
      "TWO_REQUIRED";

  useEffect(() => {
    setInvalidDates({});
    router.setParams({
      [queryParamKey]: currentMonthKey,
    });
  }, [currentMonthKey, router, queryParamKey]);

  const monthParam = selectedMonth.getMonth() + 1;
  const queryMonthParam = queryMonth.getMonth() + 1;
  const queryYearParam = queryMonth.getFullYear();
  const previousMonth = subMonths(queryMonth, 1);
  const nextMonth = addMonths(queryMonth, 1);
  const previousMonthKey = buildYearMonthKey(previousMonth);
  const nextMonthKey = buildYearMonthKey(nextMonth);
  const schoolPathSegment = currentUser?.school;

  const baseDate = new Date(queryYearParam, queryMonthParam - 1, 1);
  const visibleIntervalStart = startOfWeek(startOfMonth(baseDate), {
    weekStartsOn: 1, // Monday
  });
  const visibleIntervalEnd = addDays(
    endOfWeek(endOfMonth(baseDate), {
      weekStartsOn: 1,
    }),
    -2, // Friday
  );
  const visibleDateAfter = formatDateKey(visibleIntervalStart);
  const visibleDateBefore = formatDateKey(visibleIntervalEnd);

  const queryClosingTimes = {
    "effectiveAt[from]": visibleDateAfter,
    "effectiveAt[to]": visibleDateBefore,
    "exists[schoolClass]": String(false),
  };

  const queryHolidays = {
    "effectiveAt[from]": visibleDateAfter,
    "effectiveAt[to]": visibleDateBefore,
  };

  const queryDishPlanItems = {
    "date[after]": visibleDateAfter,
    "date[before]": visibleDateBefore,
    itemsPerPage: "200",
  };
  const queryDishPlans = {
    month: String(monthParam),
    year: String(selectedMonth.getFullYear()),
  };

  const closingTimesQuery = useCollection<ClosingTime>({
    queryKey: [
      "schoolClosingTimes",
      schoolPathSegment,
      queryYearParam,
      queryMonthParam,
    ],
    enabled: Boolean(schoolPathSegment),
    path: schoolPathSegment ? `${schoolPathSegment}/closing-times` : "",
    query: queryClosingTimes,
    staleTime: DISH_PLAN_STALE_TIME_MS,
  });

  const holidayClosingTimesQuery = useCollection<ClosingTime>({
    queryKey: [
      "schoolHolidays",
      schoolPathSegment,
      queryYearParam,
      queryMonthParam,
    ],
    path: schoolPathSegment ? `${schoolPathSegment}/holidays` : "",
    enabled: Boolean(schoolPathSegment),
    query: queryHolidays,
    staleTime: DISH_PLAN_STALE_TIME_MS,
  });
  const dishPlanItemsQuery = useCollection<DishPlanItem>({
    queryKey: ["dish-plan-items", queryYearParam, queryMonthParam],
    path: "/v1/dish-plan-items",
    query: queryDishPlanItems,
    staleTime: DISH_PLAN_STALE_TIME_MS,
  });
  const dishPlanQuery = useQuery<JsonLdCollection<DishPlan> | null>({
    queryKey: ["dish-plans", queryDishPlans],
    queryFn: async () => {
      const response = await request<JsonLdCollection<DishPlan>>(
        "/v1/dish-plans",
        {
          query: queryDishPlans,
        },
      );

      return response ?? null;
    },
    staleTime: DISH_PLAN_STALE_TIME_MS,
  });
  const dishPlanItems = dishPlanItemsQuery.items;
  const schoolClosingTimes = useMemo(
    () => [...closingTimesQuery.items, ...holidayClosingTimesQuery.items],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally not including .items because timestamp is source-of-truth
    [closingTimesQuery.dataUpdatedAt, holidayClosingTimesQuery.dataUpdatedAt],
  );
  const shouldWaitForClosingTimes =
    Boolean(schoolPathSegment) &&
    !closingTimesQuery.isError &&
    !closingTimesQuery.dataUpdatedAt;
  const shouldWaitForHolidays =
    Boolean(schoolPathSegment) &&
    !holidayClosingTimesQuery.isError &&
    !holidayClosingTimesQuery.dataUpdatedAt;

  const hasSettledDishPlanItemsQuery =
    dishPlanItemsQuery.isError || dishPlanItemsQuery.isFetched;
  const isFetched = hasSettledDishPlanItemsQuery;
  const isError = dishPlanItemsQuery.isError;

  const fallbackDeadline = useMemo(
    () => new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 3),
    [selectedMonth],
  );
  const submissionDeadlineValue =
    dishPlanQuery.data?.member?.[0]?.submissionDeadline;
  const submissionDeadline = useMemo(() => {
    if (!submissionDeadlineValue) {
      return fallbackDeadline;
    }

    const parsedSubmissionDeadline = parseISO(submissionDeadlineValue);
    return Number.isNaN(parsedSubmissionDeadline.getTime())
      ? fallbackDeadline
      : parsedSubmissionDeadline;
  }, [submissionDeadlineValue, fallbackDeadline]);

  const today = startOfToday();
  const deadlineTime = startOfDay(submissionDeadline);
  const daysRemaining = differenceInDays(deadlineTime, today);
  const editableBeforeSubmissionDeadline = daysRemaining >= 0;

  const planningMonthKeys = useMemo(() => {
    const keys = new Set<string>();

    if (!dishPlanItemsQuery.isSuccess) {
      return keys;
    }

    const monthKeysWithItems = new Set(
      dishPlanItems
        .map((item) => item.date?.slice(0, 7))
        .filter((key): key is string => Boolean(key)),
    );

    for (const key of [previousMonthKey, queryMonthKey, nextMonthKey]) {
      if (!monthKeysWithItems.has(key)) {
        keys.add(key);
      }
    }

    return keys;
  }, [
    dishPlanItemsQuery.isSuccess,
    dishPlanItems,
    previousMonthKey,
    queryMonthKey,
    nextMonthKey,
  ]);
  const isQueryMonthPlanning = planningMonthKeys.has(queryMonthKey);

  const currentMonthData = monthlyData[currentMonthKey] ?? {
    weeklyDishPlan: [],
    preselected: false,
  };

  const schoolDishPlanItemsQuery = useCollection<DishPlanItem>({
    queryKey: ["school-dish-plan-items", queryYearParam, queryMonthParam],
    path: `${schoolPathSegment}/dish-plan-items`,
    enabled: !!schoolPathSegment,
    staleTime: DISH_PLAN_STALE_TIME_MS,
    query: queryDishPlanItems,
    requestOptions: NO_STORE_COLLECTION_REQUEST_OPTIONS,
  });

  const schoolDishPlanItems = schoolDishPlanItemsQuery.items;
  const hasSettledSchoolDishPlanItems =
    !schoolPathSegment ||
    schoolDishPlanItemsQuery.isError ||
    schoolDishPlanItemsQuery.isFetched;

  const queryMonthSchoolDishPlanItemIds = useMemo(
    () => schoolDishPlanItems.map((item) => item.id),
    [schoolDishPlanItems],
  );
  const queryMonthHasPublishedFromServer = useMemo(() => {
    if (!schoolDishPlanItems.length) {
      return false;
    }
    const monthStr = String(queryMonthParam).padStart(2, "0");
    return schoolDishPlanItems.some((item) =>
      item.date?.startsWith(`${queryYearParam}-${monthStr}`),
    );
  }, [queryMonthParam, queryYearParam, schoolDishPlanItems]);

  useEffect(() => {
    if (!hasSettledSchoolDishPlanItems) {
      return;
    }

    setSchoolDishPlanItemIdsByMonth((previous) => ({
      ...previous,
      [queryMonthKey]: queryMonthSchoolDishPlanItemIds,
    }));
    setHasPublishedFromServerByMonth((previous) => ({
      ...previous,
      [queryMonthKey]: queryMonthHasPublishedFromServer,
    }));
  }, [
    hasSettledSchoolDishPlanItems,
    queryMonthKey,
    queryMonthSchoolDishPlanItemIds,
    queryMonthHasPublishedFromServer,
  ]);

  const schoolDishPlanItemIds =
    currentMonthKey === queryMonthKey
      ? queryMonthSchoolDishPlanItemIds
      : (schoolDishPlanItemIdsByMonth[currentMonthKey] ?? []);

  const hasPublishedFromServer =
    currentMonthKey === queryMonthKey
      ? queryMonthHasPublishedFromServer
      : (hasPublishedFromServerByMonth[currentMonthKey] ?? false);
  const isPlanningMonth = useMemo(() => {
    const targetMonthIndex = selectedMonth.getMonth();

    return currentMonthData.weeklyDishPlan.some((week) =>
      Object.values(week.days).some(
        (day) =>
          "type" in day &&
          day.type === "planning" &&
          day.month === targetMonthIndex,
      ),
    );
  }, [currentMonthData.weeklyDishPlan, selectedMonth]);
  const hasBeenPublished =
    !isPlanningMonth &&
    (locallyPublishedMonthKey === currentMonthKey || hasPublishedFromServer);
  const isEditingEnabled =
    editableBeforeSubmissionDeadline && !isPlanningMonth && !hasBeenPublished;
  const hasCurrentMonthSnapshot = Object.prototype.hasOwnProperty.call(
    monthlyData,
    currentMonthKey,
  );
  const hasQueryMonthSnapshot = Object.prototype.hasOwnProperty.call(
    monthlyData,
    queryMonthKey,
  );
  const shouldWaitForDishPlanQueries = !isFetched;
  const shouldWaitForSchoolDishPlanItems = !hasSettledSchoolDishPlanItems;
  const shouldWaitForMonthlySnapshot =
    dishPlanItemsQuery.isSuccess &&
    !hasQueryMonthSnapshot &&
    !shouldWaitForDishPlanQueries &&
    !shouldWaitForClosingTimes &&
    !shouldWaitForHolidays;
  const isQueryMonthReady =
    !isLoadingSchool &&
    !shouldWaitForDishPlanQueries &&
    !shouldWaitForClosingTimes &&
    !shouldWaitForHolidays &&
    !shouldWaitForSchoolDishPlanItems &&
    !shouldWaitForMonthlySnapshot;
  const isSwitchingMonth = pendingMonth !== null;
  const isInitialMonthHydration =
    !hasCurrentMonthSnapshot && !isQueryMonthReady;

  useEffect(() => {
    if (!pendingMonth || !isQueryMonthReady) {
      return;
    }

    setSelectedMonth(pendingMonth);
    setPendingMonth(null);
  }, [pendingMonth, isQueryMonthReady]);

  const weeklyData = currentMonthData.weeklyDishPlan;
  const preselected = currentMonthData.preselected;

  const selectedMenusCount = weeklyData.reduce((total, week) => {
    const dayCount = Object.values(week.days).reduce((dayTotal, day) => {
      if ("menus" in day) {
        return dayTotal + day.menus.filter((menu) => menu.isSelected).length;
      }
      return dayTotal;
    }, 0);

    return total + dayCount;
  }, 0);

  const isMealPlanComplete = () => {
    const { monthIsValid, invalidDates: newInvalidDates } = validateMealPlan(
      weeklyData,
      selectedMonth,
      isNormalSchool,
    );
    setInvalidDates(newInvalidDates);
    return monthIsValid;
  };

  const handleMenuToggle = (
    weekIndex: number,
    day: string,
    menuId: MenuItemType["id"],
  ) => {
    setMonthlyData((prevMonthlyData) => {
      const currentData = prevMonthlyData[currentMonthKey];

      if (!currentData) {
        return prevMonthlyData;
      }

      const week = currentData.weeklyDishPlan[weekIndex];

      if (!week) {
        return prevMonthlyData;
      }

      const dayData = week.days[day as keyof typeof week.days];

      if (!dayData || !("menus" in dayData)) {
        return prevMonthlyData;
      }

      const updatedMenus = dayData.menus.map((menu) =>
        menu.id === menuId ? { ...menu, isSelected: !menu.isSelected } : menu,
      );

      const newWeeklyData = [...currentData.weeklyDishPlan];
      newWeeklyData[weekIndex] = {
        ...week,
        days: {
          ...week.days,
          [day]: {
            ...dayData,
            menus: updatedMenus,
          },
        },
      };

      return {
        ...prevMonthlyData,
        [currentMonthKey]: {
          ...currentData,
          weeklyDishPlan: newWeeklyData,
        },
      };
    });
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    setPendingMonth((currentPendingMonth) => {
      const baseMonth = currentPendingMonth ?? selectedMonth;
      const newMonth = new Date(baseMonth);
      if (direction === "prev") {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const formattedMonth = selectedMonth.toLocaleDateString(i18n.language, {
    month: "long",
    year: "numeric",
  });

  const formattedMonthOnly = selectedMonth.toLocaleDateString(i18n.language, {
    month: "long",
  });

  const loadingWeekCount = useMemo(() => {
    const loadingStart = startOfWeek(startOfMonth(selectedMonth), {
      weekStartsOn: 1,
    });
    const loadingEnd = endOfWeek(endOfMonth(selectedMonth), {
      weekStartsOn: 1,
    });

    return Math.ceil(
      eachDayOfInterval({ start: loadingStart, end: loadingEnd }).length / 7,
    );
  }, [selectedMonth]);

  const preselectWeeklyData = (
    weeklyData: WeeklyMenuData[],
  ): WeeklyMenuData[] => {
    const parsed = JSON.parse(JSON.stringify(weeklyData)) as WeeklyMenuData[];
    return parsed.map((week) => {
      const days = week.days;
      const dayKeys = Object.keys(days) as (keyof WeeklyMenuData["days"])[];
      dayKeys.forEach((dayKey) => {
        if (
          "menus" in days[dayKey] &&
          days[dayKey].month === selectedMonth.getMonth()
        ) {
          const dayHasNonVegetarian = days[dayKey].menus.some(
            (menu) => menu.label !== "vegetarian",
          );
          days[dayKey].menus = days[dayKey].menus.map((menu, menuIndex) => {
            menu.isSelected = false;
            if (menuIndex === 0) menu.isSelected = true;
            if (isNormalSchool && menuIndex === 1) menu.isSelected = true;
            if (dayHasNonVegetarian && !isNormalSchool && menuIndex === 1)
              menu.isSelected = true;
            return menu;
          });
        }
      });
      return {
        ...week,
        days,
      };
    });
  };

  const handlePreselected = (value: boolean) => {
    setMonthlyData((prevMonthlyData) => ({
      ...prevMonthlyData,
      [currentMonthKey]: {
        weeklyDishPlan: preselectWeeklyData(
          prevMonthlyData[currentMonthKey]?.weeklyDishPlan ?? [],
        ),
        preselected: value,
      },
    }));
  };

  const handlePublish = async () => {
    if (isMealPlanComplete()) {
      if (!schoolPathSegment) {
        setPublishErrorMessage(t("mealPlan.publishRequestErrorDescription"));
        setPublishModalState("requestError");
        return;
      }

      const payload = buildSchoolDishPlanPayload(weeklyData, selectedMonth);

      setIsPublishing(true);

      try {
        await request<SchoolDishPlan>(`${schoolPathSegment}/dish-plans`, {
          method: "POST",
          headers: {
            "Content-Type": "application/ld+json",
          },
          body: JSON.stringify(payload),
        });

        await schoolDishPlanItemsQuery.refetch();
        setPublishErrorMessage(null);
        setPublishModalState("success");
        setLocallyPublishedMonthKey(currentMonthKey);
      } catch (error) {
        console.error(
          "publish school-dish-plan failed with",
          error instanceof Error ? error.message : error,
        );
        setPublishErrorMessage(t("mealPlan.publishRequestErrorDescription"));
        setPublishModalState("requestError");
      } finally {
        setIsPublishing(false);
      }

      return;
    }

    setPublishErrorMessage(null);
    setPublishModalState("validationError");
  };

  const closePublishModal = () => {
    setPublishModalState(null);
    setPublishErrorMessage(null);
  };

  const handleSuccessCtaPress = () => {
    closePublishModal();
    setTimeout(scrollToTop, 250); // Wait for Modal's closing animation to finish.
  };

  useEffect(() => {
    if (!isFetched) {
      return;
    }

    if (shouldWaitForClosingTimes || shouldWaitForHolidays) {
      return;
    }

    const hasAnyDishPlanItems = dishPlanItems.length > 0;

    if (hasAnyDishPlanItems || isQueryMonthPlanning) {
      setMonthlyData((prevMonthlyData) => {
        const previousWeeks =
          prevMonthlyData[queryMonthKey]?.weeklyDishPlan ?? [];
        const selections = buildMenuSelectionMap(previousWeeks);
        const nextWeeks = buildWeeklyMenuData(
          [dishPlanItems],
          queryMonth,
          schoolClosingTimes,
          planningMonthKeys,
        );

        return {
          ...prevMonthlyData,
          [queryMonthKey]: {
            weeklyDishPlan: applyMenuSelections(nextWeeks, selections),
            preselected: prevMonthlyData[queryMonthKey]?.preselected ?? false,
          },
        };
      });
    } else {
      setMonthlyData((prevMonthlyData) => ({
        ...prevMonthlyData,
        [queryMonthKey]: {
          weeklyDishPlan: [],
          preselected: prevMonthlyData[queryMonthKey]?.preselected ?? false,
        },
      }));
    }
  }, [
    queryMonthKey,
    isFetched,
    dishPlanItems,
    queryMonth,
    schoolPathSegment,
    closingTimesQuery.dataUpdatedAt,
    closingTimesQuery.isError,
    holidayClosingTimesQuery.dataUpdatedAt,
    holidayClosingTimesQuery.isError,
    schoolClosingTimes,
    shouldWaitForClosingTimes,
    shouldWaitForHolidays,
    isQueryMonthPlanning,
    planningMonthKeys,
  ]);

  const queryDishPlanDocuments = {
    month: String(queryMonthParam),
    year: String(queryYearParam),
  };

  const dishPlanDocumentsQuery = useCollection<DishPlanDocument>({
    queryKey: ["dish-plan-documents", queryYearParam, queryMonthParam],
    path: "/v1/dish-plan-documents",
    query: queryDishPlanDocuments,
    staleTime: DISH_PLAN_STALE_TIME_MS,
  });

  const schoolDishPlanDocumentsQuery = useCollection<DishPlanDocument>({
    queryKey: [
      "dish-plan-documents",
      queryYearParam,
      queryMonthParam,
      schoolPathSegment,
    ],
    path: "/v1/dish-plan-documents",
    query: { ...queryDishPlanDocuments, school: schoolPathSegment! },
    staleTime: DISH_PLAN_STALE_TIME_MS,
    enabled: !!schoolPathSegment,
  });
  const dishPlanDocument: DishPlanDocument | undefined =
    schoolDishPlanDocumentsQuery.items[0] ?? dishPlanDocumentsQuery.items[0];

  const header = (
    <MealplanHeader
      onMonthChange={handleMonthChange}
      isEditingAllowed={
        !isInitialMonthHydration && !isSwitchingMonth && isEditingEnabled
      }
      daysRemaining={daysRemaining}
      formattedMonthOnly={formattedMonthOnly}
      formattedMonth={formattedMonth}
      maxWidth={width}
    />
  );

  const lang = i18n.language.split("-")[0] as "de"; // de-DE -> de

  return (
    <>
      <Head>
        <title>{t("titles.mealPlan")}</title>
      </Head>
      <PageContainer
        header={header}
        maxWidth={maxWidth}
        style={[styles.container, { backgroundColor: colors.background }]}
      >
        {!isInitialMonthHydration && (
          <PreselectionContainer
            isEditingAllowed={isEditingEnabled && !isSwitchingMonth}
            hasBeenPublished={hasBeenPublished}
            isNormalSchool={isNormalSchool}
            formattedMonthOnly={formattedMonthOnly}
            preselected={preselected}
            setPreselected={handlePreselected}
          />
        )}
        {!isInitialMonthHydration && hasBeenPublished && (
          <View style={styles.editingNotice}>
            <Text
              style={[
                styles.editingPrompt,
                fonts.regular,
                { color: colors.text },
              ]}
            >
              {t("mealPlan.changeNotice")}
            </Text>
            <View style={styles.editingActions}>
              <SecondaryButton
                label={t("mealPlan.changeActionAddClosingTime", {
                  month: formattedMonthOnly,
                })}
                onPress={() => {
                  router.push(`/${lang}/schliesszeiten`);
                }}
                iconName="arrow-right"
                iconPosition="right"
                style={styles.editingAction}
              />
              <SecondaryButton
                label={t("mealPlan.changeActionAddGuestOrder", {
                  month: formattedMonthOnly,
                })}
                onPress={() => {
                  router.push(`/${lang}/gastbestellungen`);
                }}
                iconName="arrow-right"
                iconPosition="right"
                style={styles.editingAction}
              />
              <SecondaryButton
                label={t("mealPlan.changeActionOrderLunchBoxesJuly", {
                  month: formattedMonthOnly,
                })}
                onPress={() => {
                  router.push(`/${lang}/lunchpakete`);
                }}
                iconName="arrow-right"
                iconPosition="right"
                style={styles.editingAction}
              />
            </View>
          </View>
        )}

        <WeeklyGridHeader />

        {isInitialMonthHydration && (
          <WeeklyGridLoadingSkeleton weekCount={loadingWeekCount} />
        )}

        {weeklyData.length === 0 && !isInitialMonthHydration && isFetched && (
          <View style={styles.feedbackContainer}>
            <Text
              style={[
                styles.emptyStateText,
                { color: colors.text, ...fonts.medium },
              ]}
            >
              {isError
                ? t("mealPlan.errorLoadingMenuPlan")
                : t("mealPlan.noData")}
            </Text>
            {!isError && (
              <Link
                push
                asChild
                href={`/de/speiseplan?${queryParamKey}=${buildYearMonthKey(new Date())}`}
                style={styles.noDataLink}
              >
                <SecondaryButton label={t("mealPlan.noDataLinkText")} />
              </Link>
            )}
          </View>
        )}

        {!isInitialMonthHydration && weeklyData.length > 0 ? (
          <WeeklyGrid
            weeks={weeklyData}
            isReadOnly={!isEditingEnabled || isSwitchingMonth}
            hasBeenPublished={hasBeenPublished}
            style={styles.weeklyGrid}
            onMenuToggle={handleMenuToggle}
            invalidDates={invalidDates}
            month={monthParam}
            selectedMenuIds={schoolDishPlanItemIds}
            disableAll={isPlanningMonth}
            dishPlanDocumentUrl={
              dishPlanDocument?.document
                ? `${dishPlanDocument.document}/${t(
                    schoolDishPlanDocumentsQuery.items[0]
                      ? "common.allergenesAndAdditivesSchoolDownloadFileName"
                      : "common.allergenesAndAdditivesCommonDownloadFileName",
                    {
                      month: t(`common.months.${queryMonthParam}`),
                      year: queryYearParam,
                    },
                  )}?download=true`
                : undefined
            }
          />
        ) : null}

        {!isInitialMonthHydration &&
          isEditingEnabled &&
          !isSwitchingMonth &&
          weeklyData.length > 0 && (
            <View style={styles.publishContainer}>
              <Text
                style={[
                  styles.publishTitle,
                  fonts.regular,
                  { color: colors.palette.spinach30 },
                ]}
              >
                {t("mealPlan.publishChecklistTitle")}
              </Text>
              <Text
                style={[
                  styles.publishSummary,
                  fonts.semiBold,
                  { color: colors.black },
                ]}
              >
                {t("mealPlan.publishSelectionSummary", {
                  count: selectedMenusCount,
                })}
              </Text>
              <View style={styles.noticeBoxWrapper}>
                <NoticeBox
                  title={t("common.notice")}
                  textContent={t("mealPlan.publishNotice")}
                />
              </View>
              <Button
                buttonText={
                  isPublishing
                    ? t("common.submitting")
                    : t("mealPlan.publishButton")
                }
                onPress={handlePublish}
                disabled={isPublishing}
                buttonStyle={[{ backgroundColor: colors.border }]}
                buttonTextStyle={[fonts.semiBold, { color: colors.background }]}
                iconName="arrow-right"
                iconColor={colors.background}
                iconPosition="right"
              />
            </View>
          )}

        {!isInitialMonthHydration && isPlanningMonth && (
          <View style={styles.belowMealPlanNotice}>
            <Text
              style={[
                styles.modalDescription,
                fonts.regular,
                { color: colors.border },
              ]}
            >
              {t("mealPlan.noticePlanningMonth")}
            </Text>
            <ThemedText style={fonts.semiBold} type="title">
              {t("mealPlan.noticePlanningMonthEmail")}
            </ThemedText>
          </View>
        )}
      </PageContainer>
      {!isInitialMonthHydration && hasBeenPublished && (
        <ContactBanner style={styles.contactBanner} />
      )}
      <ChefHatModal
        visible={publishModalState === "success"}
        icon={{ icon: "spoon", iconSet: "font-awesome" }}
        iconSize={48}
        maxWidth={1_100}
        onDismiss={closePublishModal}
      >
        <View style={styles.modalHeader}>
          <Icon
            iconSet="material-community"
            icon="check-circle"
            size={24}
            color={colors.success}
          />
          <Text
            style={[
              styles.modalHeaderText,
              fonts.bold,
              { color: colors.success },
            ]}
          >
            {t("mealPlan.publishSuccessTitle")}
          </Text>
        </View>
        <Text style={[styles.modalTitle, fonts.bold, { color: colors.text }]}>
          {t("mealPlan.publishSuccessSubtitle")}
        </Text>
        <Text
          style={[
            styles.modalDescription,
            fonts.regular,
            { color: colors.text },
          ]}
        >
          {t("mealPlan.publishSuccessDescription", {
            month: formattedMonthOnly,
          })}
        </Text>
        <View style={styles.modalActions}>
          <SecondaryButton
            label={t("mealPlan.publishSuccessCta")}
            onPress={handleSuccessCtaPress}
            iconName="arrow-right"
            iconPosition="right"
          />
        </View>
      </ChefHatModal>
      <ChefHatModal
        visible={
          publishModalState === "validationError" ||
          publishModalState === "requestError"
        }
        icon={{ icon: "spoon", iconSet: "font-awesome" }}
        iconSize={48}
        maxWidth={1_100}
        onDismiss={closePublishModal}
      >
        <View style={styles.modalHeader}>
          <Icon
            iconSet="font-awesome"
            icon="bell-exclamation"
            size={24}
            color={colors.danger}
          />
          <Text
            style={[
              styles.modalHeaderText,
              fonts.bold,
              { color: colors.danger },
            ]}
          >
            {t("mealPlan.publishValidationTitle")}
          </Text>
        </View>
        <Text style={[styles.modalTitle, fonts.bold, { color: colors.text }]}>
          {publishModalState === "validationError"
            ? t("mealPlan.publishValidationSubtitle")
            : t("mealPlan.publishRequestErrorSubtitle")}
        </Text>
        <Text
          style={[
            styles.modalDescription,
            fonts.regular,
            { color: colors.text },
          ]}
        >
          {publishModalState === "validationError"
            ? t("mealPlan.publishValidationDescription")
            : (publishErrorMessage ??
              t("mealPlan.publishRequestErrorDescription"))}
        </Text>
        <View style={styles.modalActions}>
          <Button
            buttonText={
              publishModalState === "validationError"
                ? t("mealPlan.publishValidationCta")
                : t("mealPlan.publishRequestErrorCta")
            }
            onPress={closePublishModal}
            buttonStyle={[
              styles.modalPrimaryButton,
              { backgroundColor: colors.border },
            ]}
            buttonTextStyle={[
              styles.modalPrimaryButtonText,
              fonts.bold,
              { color: colors.background },
            ]}
            iconName="arrow-right"
            iconColor={colors.background}
            iconPosition="right"
            iconSize={24}
          />
        </View>
      </ChefHatModal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 32,
  },
  feedbackContainer: {
    paddingVertical: 24,
    alignItems: "center",
    gap: 12,
  },
  emptyStateText: {
    fontSize: 14,
    textAlign: "center",
  },
  weeklyGrid: {
    marginBottom: 40,
  },
  publishContainer: {
    alignItems: "center",
    maxWidth: 986,
    marginTop: 88,
    marginBottom: 96,
    marginHorizontal: "auto",
  },
  publishTitle: {
    fontSize: 20,
    lineHeight: 25,
    textAlign: "center",
  },
  publishSummary: {
    fontSize: 32,
    lineHeight: 40,
    textAlign: "center",
    marginTop: 24,
  },
  editingNotice: {
    marginTop: 40,
    marginBottom: 32,
    width: "100%",
  },
  editingPrompt: {
    fontSize: 24,
    lineHeight: 30,
  },
  editingActions: {
    marginTop: 24,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  editingAction: {
    minWidth: 280,
  },
  noticeBoxWrapper: {
    marginTop: 40,
    marginBottom: 48,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 24,
  },
  modalHeaderText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  modalTitle: {
    fontSize: 32,
    lineHeight: 40,
    marginBottom: 24,
  },
  modalDescription: {
    fontSize: 20,
    lineHeight: 25,
  },
  modalActions: {
    marginTop: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 16,
  },
  modalPrimaryButton: {
    borderRadius: 56,
    height: 56,
    paddingHorizontal: 24,
  },
  modalPrimaryButtonText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  contactBanner: {
    marginTop: 36,
  },
  noDataLink: {
    marginTop: 32,
  },
  belowMealPlanNotice: {
    marginTop: 48,
    marginBottom: 96,
    flexDirection: "column",
    alignItems: "center",
    gap: 24,
  },
});
